import { CertificationTooltipComponent, CertificationTooltipProps } from '@tableau/certification-watermark';
import { SiteUrls } from '@tableau/shared-urls';
import React from 'react';
import { SvgSpacesMediumExtractCertified, SvgSpacesMediumDsCertified, SvgSpacesMediumDs, SvgSpacesMediumExtract } from '../../../../../../gen-src/icons';
import { getCertifierPropsFromCertifier } from '../../CertificationWidget';
import { TableauUser } from '../../UserDisplayComponent';
import { divWithStyle } from '../../style/LayoutComponents';
import { FormattedMarkupWrapperInTooltip } from '../../style/MarkupStyles';
import { SiteUrlsContext } from '../../urls/SiteUrlsContext';

const CertifiedIconWrapper = divWithStyle('CertifiedIconWrapper', {
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
});

interface DatasourceTypeCellProps {
  readonly isCertified: boolean;
  readonly hasExtracts: boolean;
  readonly certifier: TableauUser | null;
  readonly certificationNote?: string;
  readonly extractLastUpdateTime?: Date;
  readonly linksDisabled?: boolean;
  readonly zIndex?: number;
}

/** If the data source is certified, the certifier isn't null, which we rely upon when computing the tooltip props. */
type CertifiedDatasourceTypeCellProps =
  DatasourceTypeCellProps & { certifier: NonNullable<DatasourceTypeCellProps['certifier']> };

function isCertified(props: DatasourceTypeCellProps): props is CertifiedDatasourceTypeCellProps {
  return props.isCertified && !!props.certifier;
}

function getCertifiedIconWithWrapper(hasExtracts: boolean) {
  return (
    <CertifiedIconWrapper>
      {hasExtracts ? <SvgSpacesMediumExtractCertified /> : <SvgSpacesMediumDsCertified />}
    </CertifiedIconWrapper>
  );
}

/** @deprecated */
function getCertificationTooltipProps(
  props: CertifiedDatasourceTypeCellProps, urls?: SiteUrls,
): CertificationTooltipProps {

  const certificationNote = props.certificationNote || '';
  const note = <FormattedMarkupWrapperInTooltip markup={certificationNote} />;

  const certificationTooltipProps: CertificationTooltipProps = {
    certifier: getCertifierPropsFromCertifier(props.certifier, urls),
    note: note,
    icon: getCertifiedIconWithWrapper(props.hasExtracts),
    showExtractRefresh: true,
    date: props.extractLastUpdateTime,
    linksDisabled: props.linksDisabled || false,
    zIndex: props.zIndex || undefined,
  };

  return certificationTooltipProps;
}

/** @deprecated don't use certification props */
export function DatasourceTypeCell(props: DatasourceTypeCellProps): JSX.Element {
  if (isCertified(props)) {
    if (props.linksDisabled) {
      return <CertificationTooltipComponent {...getCertificationTooltipProps(props)} />;
    } else {
      return (
        <SiteUrlsContext.Consumer>
          {urls => <CertificationTooltipComponent {...getCertificationTooltipProps(props, urls)} />}
        </SiteUrlsContext.Consumer>
      );
    }
  } else {
    if (props.hasExtracts) {
      return <SvgSpacesMediumExtract />;
    } else {
      return <SvgSpacesMediumDs />;
    }
  }
}

/** @deprecated don't use non-labels managed certification components */
export function GenerateCertificationTooltipComponent(isCertified: boolean, lastExtract: string | null,
  hasExtracts: boolean, certifier: TableauUser | null,
  certificationNote: string | undefined, linksDisabled?: boolean, zIndex?: number) {

  const date = lastExtract ? new Date(lastExtract) : undefined;
  const props: DatasourceTypeCellProps = {
    isCertified: isCertified,
    hasExtracts: hasExtracts,
    certifier: certifier,
    extractLastUpdateTime: date,
    certificationNote: certificationNote,
    linksDisabled: linksDisabled,
    zIndex: zIndex,
  };

  return (
    <DatasourceTypeCell {...props} />
  );
}
